import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { affiliateAPI } from '../services/affiliateApi';
import { toast } from 'react-hot-toast';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  LinkIcon,
  ArrowRightIcon,
  SparklesIcon,
  ClockIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

const AffiliatesLanding = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isAuthenticated, refreshProfile } = useAuth();
  const [affiliateStatus, setAffiliateStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Signup form state
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    full_name: '',
    application_notes: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch affiliate status if logged in
  useEffect(() => {
    if (isAuthenticated) {
      fetchAffiliateStatus();
    }
  }, [isAuthenticated]);

  const fetchAffiliateStatus = async () => {
    try {
      setLoading(true);
      const response = await affiliateAPI.getStatus();
      setAffiliateStatus(response.data);
    } catch (error) {
      console.error('Error fetching affiliate status:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!signupData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(signupData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!signupData.password) {
      newErrors.password = 'Password is required';
    } else if (signupData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      const response = await affiliateAPI.signup(signupData);
      
      // Store token and user info in localStorage
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Update local state immediately to show pending status
      setAffiliateStatus({
        is_affiliate: false,
        application_status: 'pending',
      });
      setShowSignupForm(false);
      
      toast.success('Application submitted! We\'ll review it within 24-48 hours.');
      
      // Try to refresh auth context (but don't fail if it errors)
      try {
        await refreshProfile();
      } catch (refreshError) {
        console.log('Profile refresh skipped - user will be loaded on next page visit');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit application';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/affiliate-dashboard');
  };

  // Handler for existing users to apply
  const handleApplyExistingUser = async () => {
    try {
      setSubmitting(true);
      await affiliateAPI.apply(signupData.application_notes || null);
      toast.success('Application submitted! We\'ll review it within 24-48 hours.');
      fetchAffiliateStatus();
      setShowSignupForm(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit application';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: CurrencyDollarIcon,
      title: t('affiliate.landing.benefits.earnCommissions.title'),
      description: t('affiliate.landing.benefits.earnCommissions.description'),
    },
    {
      icon: ChartBarIcon,
      title: t('affiliate.landing.benefits.realTimeAnalytics.title'),
      description: t('affiliate.landing.benefits.realTimeAnalytics.description'),
    },
    {
      icon: LinkIcon,
      title: t('affiliate.landing.benefits.customTracking.title'),
      description: t('affiliate.landing.benefits.customTracking.description'),
    },
    {
      icon: ClockIcon,
      title: t('affiliate.landing.benefits.cookie.title'),
      description: t('affiliate.landing.benefits.cookie.description'),
    },
    {
      icon: ShieldCheckIcon,
      title: t('affiliate.landing.benefits.reliablePayouts.title'),
      description: t('affiliate.landing.benefits.reliablePayouts.description'),
    },
    {
      icon: GlobeAltIcon,
      title: t('affiliate.landing.benefits.globalReach.title'),
      description: t('affiliate.landing.benefits.globalReach.description'),
    },
  ];

  const howItWorks = [
    {
      step: 1,
      title: t('affiliate.landing.howItWorks.step1.title'),
      description: t('affiliate.landing.howItWorks.step1.description'),
    },
    {
      step: 2,
      title: t('affiliate.landing.howItWorks.step2.title'),
      description: t('affiliate.landing.howItWorks.step2.description'),
    },
    {
      step: 3,
      title: t('affiliate.landing.howItWorks.step3.title'),
      description: t('affiliate.landing.howItWorks.step3.description'),
    },
  ];

  const faqs = [
    {
      question: t('affiliate.landing.faq.howMuch.question'),
      answer: t('affiliate.landing.faq.howMuch.answer'),
    },
    {
      question: t('affiliate.landing.faq.whenPaid.question'),
      answer: t('affiliate.landing.faq.whenPaid.answer'),
    },
    {
      question: t('affiliate.landing.faq.trackReferrals.question'),
      answer: t('affiliate.landing.faq.trackReferrals.answer'),
    },
    {
      question: t('affiliate.landing.faq.socialMedia.question'),
      answer: t('affiliate.landing.faq.socialMedia.answer'),
    },
    {
      question: t('affiliate.landing.faq.materials.question'),
      answer: t('affiliate.landing.faq.materials.answer'),
    },
  ];

  // Render CTA based on status
  const renderCTA = () => {
    if (loading) {
      return (
        <div className="animate-pulse bg-white/20 h-14 w-48 rounded-xl mx-auto"></div>
      );
    }

    // Approved affiliate - show dashboard button
    if (affiliateStatus?.is_affiliate) {
      return (
        <button
          onClick={handleGoToDashboard}
          className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl hover:from-green-600 hover:to-emerald-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
        >
          {t('affiliate.landing.goToDashboard')}
          <ArrowRightIcon className="ml-2 h-5 w-5" />
        </button>
      );
    }

    // Pending application - show status
    if (affiliateStatus?.application_status === 'pending' || (!isAuthenticated && affiliateStatus?.application_status === 'pending')) {
      return (
        <div className="max-w-md mx-auto text-center">
          <div className="bg-amber-500/20 backdrop-blur-lg rounded-2xl p-8 border border-amber-400/30">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ClockIcon className="h-8 w-8 text-amber-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">{t('affiliate.landing.application.underReview')}</h3>
            <p className="text-gray-300 mb-4">
              {t('affiliate.landing.application.underReviewMessage')}
            </p>
            <p className="text-sm text-gray-400">
              {t('affiliate.landing.application.emailNotification')}
            </p>
          </div>
        </div>
      );
    }

    // Show application form for logged-in users
    if (showSignupForm && isAuthenticated) {
      return (
        <div className="max-w-md mx-auto bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
          <h3 className="text-2xl font-bold text-white text-center mb-2">{t('affiliate.landing.application.applyTitle')}</h3>
          <p className="text-gray-400 text-center mb-6">{t('affiliate.landing.application.welcomeBack', { name: user?.full_name || user?.email })}</p>
          
          <div className="space-y-4">
            {/* Application Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('affiliate.landing.application.howPromote')} <span className="text-gray-500">{t('affiliate.landing.application.optional')}</span>
              </label>
              <textarea
                value={signupData.application_notes}
                onChange={(e) => setSignupData({ ...signupData, application_notes: e.target.value })}
                placeholder={t('affiliate.landing.application.promotePlaceholder')}
                rows={4}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400 resize-none"
              />
            </div>

            {/* Submit Button */}
            <button
              onClick={handleApplyExistingUser}
              disabled={submitting}
              className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 transition-all flex items-center justify-center"
            >
              {submitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('affiliate.landing.application.submitting')}
                </>
              ) : (
                <>
                  {t('affiliate.landing.application.submitApplication')}
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </>
              )}
            </button>

            {/* Cancel Link */}
            <button
              type="button"
              onClick={() => setShowSignupForm(false)}
              className="w-full py-2 text-gray-400 hover:text-white transition-colors text-sm"
            >
              {t('affiliate.landing.application.cancel')}
            </button>
          </div>
        </div>
      );
    }

    // Show signup form for new users
    if (showSignupForm && !isAuthenticated) {
      return (
        <div className="max-w-md mx-auto bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
          <h3 className="text-2xl font-bold text-white text-center mb-2">{t('affiliate.landing.application.joinTitle')}</h3>
          <p className="text-gray-400 text-center mb-6">{t('affiliate.landing.application.createAccount')}</p>
          
          <form onSubmit={handleSignup} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.landing.application.fullName')}</label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input
                  type="text"
                  value={signupData.full_name}
                  onChange={(e) => setSignupData({ ...signupData, full_name: e.target.value })}
                  placeholder="John Doe"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.landing.application.email')}</label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input
                  type="email"
                  value={signupData.email}
                  onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                  placeholder="you@example.com"
                  className={`w-full pl-10 pr-4 py-3 bg-white/10 border ${errors.email ? 'border-red-400' : 'border-white/20'} rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400`}
                />
              </div>
              {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.landing.application.password')}</label>
              <div className="relative">
                <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={signupData.password}
                  onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                  placeholder="••••••••"
                  className={`w-full pl-10 pr-12 py-3 bg-white/10 border ${errors.password ? 'border-red-400' : 'border-white/20'} rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && <p className="text-red-400 text-sm mt-1">{errors.password}</p>}
            </div>

            {/* Application Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('affiliate.landing.application.howPromote')} <span className="text-gray-500">{t('affiliate.landing.application.optional')}</span>
              </label>
              <textarea
                value={signupData.application_notes}
                onChange={(e) => setSignupData({ ...signupData, application_notes: e.target.value })}
                placeholder={t('affiliate.landing.application.promotePlaceholder')}
                rows={3}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400 resize-none"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 transition-all flex items-center justify-center"
            >
              {submitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('affiliate.landing.application.submitting')}
                </>
              ) : (
                <>
                  {t('affiliate.landing.application.submitApplication')}
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </>
              )}
            </button>

            {/* Cancel Link */}
            <button
              type="button"
              onClick={() => setShowSignupForm(false)}
              className="w-full py-2 text-gray-400 hover:text-white transition-colors text-sm"
            >
              {t('affiliate.landing.application.cancel')}
            </button>

            {/* Login Link */}
            <p className="text-center text-gray-400 text-sm">
              {t('affiliate.landing.application.alreadyHaveAccount')}{' '}
              <button
                type="button"
                onClick={() => navigate('/login?redirect=/affiliates')}
                className="text-pink-400 hover:text-pink-300"
              >
                {t('affiliate.landing.application.loginHere')}
              </button>
            </p>
          </form>
        </div>
      );
    }

    // Default - show apply button
    return (
      <button
        onClick={() => setShowSignupForm(true)}
        className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl hover:from-pink-600 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
      >
        {t('affiliate.landing.applyNow')}
        <ArrowRightIcon className="ml-2 h-5 w-5" />
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-40 right-20 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
            <SparklesIcon className="h-5 w-5 text-yellow-400 mr-2" />
            <span className="text-white text-sm font-medium">{t('affiliate.landing.badge')}</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6">
            {t('affiliate.landing.heroTitle')}
            <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent"> {t('affiliate.landing.heroTitleHighlight')}</span>
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10">
            {t('affiliate.landing.heroSubtitle')}
          </p>
          
          {renderCTA()}
          
          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '30', label: t('affiliate.landing.stats.dayCookie') },
              { value: '$$$', label: t('affiliate.landing.stats.perSignup') },
              { value: '24/7', label: t('affiliate.landing.stats.tracking') },
              { value: t('pricing.monthly'), label: t('affiliate.landing.stats.payouts') },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white">{stat.value}</div>
                <div className="text-gray-400 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('affiliate.landing.benefits.title')}
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              {t('affiliate.landing.benefits.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-pink-400/30 transition-colors"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-400">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('affiliate.landing.howItWorks.title')}
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              {t('affiliate.landing.howItWorks.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative text-center">
                {/* Connection line */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-pink-500/50 to-purple-500/50"></div>
                )}
                
                <div className="relative z-10 w-24 h-24 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-4xl font-bold text-white">{step.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-gray-400">{step.description}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            {renderCTA()}
          </div>
        </div>
      </section>

      {/* Dashboard Preview Section */}
      <section className="py-20 bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('affiliate.landing.dashboard.title')}
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              {t('affiliate.landing.dashboard.subtitle')}
            </p>
          </div>
          
          {/* Mock Dashboard Preview */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: t('affiliate.landing.dashboard.totalClicks'), value: '1,234', color: 'text-blue-400' },
                { label: t('affiliate.landing.dashboard.signups'), value: '89', color: 'text-green-400' },
                { label: t('affiliate.landing.dashboard.upgrades'), value: '23', color: 'text-purple-400' },
                { label: t('affiliate.landing.dashboard.earnings'), value: '$456.00', color: 'text-pink-400' },
              ].map((stat, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-4 text-center">
                  <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                  <div className="text-gray-400 text-sm mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
            
            {/* Mock Chart */}
            <div className="bg-white/5 rounded-xl p-4">
              <div className="flex items-end justify-between h-32 px-4">
                {[40, 65, 45, 80, 55, 90, 70].map((height, index) => (
                  <div
                    key={index}
                    className="w-8 bg-gradient-to-t from-pink-500 to-purple-500 rounded-t-lg"
                    style={{ height: `${height}%` }}
                  ></div>
                ))}
              </div>
              <div className="flex justify-between text-gray-500 text-xs mt-2 px-2">
                <span>Mon</span>
                <span>Tue</span>
                <span>Wed</span>
                <span>Thu</span>
                <span>Fri</span>
                <span>Sat</span>
                <span>Sun</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('affiliate.landing.faq.title')}
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <span className="text-white font-medium">{faq.question}</span>
                  <span className="text-gray-400 group-open:rotate-180 transition-transform">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-400">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-pink-600/20 to-purple-600/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('affiliate.landing.cta.title')}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t('affiliate.landing.cta.subtitle')}
          </p>
          {renderCTA()}
        </div>
      </section>

      <Footer variant="dark" />
    </div>
  );
};

export default AffiliatesLanding;
